import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { clubApi } from "../../club-api-client";
import type { CreateTrainingGroup, UpdateTrainingGroup } from "../../types";
import { queryKeys } from "../query-keys";

export function useTrainingGroups(clubId: string | null, seasonId?: string) {
	return useQuery({
		enabled: !!clubId,
		queryFn: () => clubApi.training.groups.list(clubId!, seasonId),
		queryKey: queryKeys.clubPortal.trainingGroups(clubId ?? "", seasonId),
	});
}

export function useTrainingGroup(clubId: string | null, groupId: string | null) {
	return useQuery({
		enabled: !!clubId && !!groupId,
		queryFn: () => clubApi.training.groups.get(clubId!, groupId!),
		queryKey: queryKeys.clubPortal.trainingGroup(clubId ?? "", groupId ?? ""),
	});
}

export function useCreateTrainingGroup(clubId: string) {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: ({ seasonId, data }: { seasonId: string; data: CreateTrainingGroup }) =>
			clubApi.training.groups.create(clubId, seasonId, data),
		onSuccess: () => {
			qc.invalidateQueries({
				queryKey: ["club-portal", "clubs", clubId, "training"],
			});
			qc.invalidateQueries({
				queryKey: queryKeys.clubPortal.clubSquads(clubId),
			});
		},
	});
}

export function useUpdateTrainingGroup(clubId: string) {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: ({ groupId, data }: { groupId: string; data: UpdateTrainingGroup }) =>
			clubApi.training.groups.update(clubId, groupId, data),
		onSuccess: () =>
			qc.invalidateQueries({
				queryKey: ["club-portal", "clubs", clubId, "training"],
			}),
	});
}

export function useDeleteTrainingGroup(clubId: string) {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (groupId: string) => clubApi.training.groups.delete(clubId, groupId),
		onSuccess: () =>
			qc.invalidateQueries({
				queryKey: ["club-portal", "clubs", clubId, "training"],
			}),
	});
}
